import { differenceInDays, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import posthog from 'posthog-js';

import { membershipRuleKeys } from '../../constants/membership';
import { Event } from '../../types/event';
import { Ticket } from '../../types/ticket';
import { Rule } from '../types/Rule';
import { RuleContext } from '../types/RuleContext';

export class CheckRatingBasedTicketAccessRule implements Rule<Ticket[]> {
  private static readonly RATING_DAYS_MAP = {
    1 : 7,
    2 : 6,
    3 : 5,
  } as const;

  private static readonly DEFAULT_RATING = 3;
  private static readonly ESQUADRAO_PREFIX = 'ESQUADRAONAFONTE';

  apply(context: RuleContext): Ticket[] {
    const { tickets, membership, event, updateMembershipError } = context;

    // Verify feature flag
    const isRatingRuleEnabled = posthog.isFeatureEnabled?.('enable-bahia-rating');
    if (!isRatingRuleEnabled) {
      return tickets;
    }

    // Only apply when we have membership data and tickets
    if (!membership || !tickets) {
      return tickets;
    }

    if (!this.isEsquadraoNaFonte(membership.name)) {
      return tickets;
    }

    const referenceDate = this.getDateInEventTimezone(membership.timestamp!, event);

    // Filter tickets and check if user has access
    const hasAccess = this.hasAccessToTicketDates(event, membership.rating!, referenceDate);

    if(!hasAccess) {
      updateMembershipError?.(membershipRuleKeys.priorityRestricted);
      console.log('updateMembershipError', membershipRuleKeys.priorityRestricted);
      return [];
    }

    return tickets;
  }

  private isEsquadraoNaFonte(membershipName: string): boolean {
    return membershipName?.startsWith(CheckRatingBasedTicketAccessRule.ESQUADRAO_PREFIX);
  }

  private getDateInEventTimezone(date: string, event: Event): Date {
    if (!date) {
      return new Date();
    }

    const membershipDateTime = parseISO(date);
    const eventTimezone = event?.timezone || 'America/Sao_Paulo';

    return toZonedTime(membershipDateTime, eventTimezone);
  }

  private hasAccessToTicketDates(
    event: Event,
    rating: number,
    referenceDate: Date
  ): boolean {
    return event.sessions.map((session) => {
      const sessionDate = this.getDateInEventTimezone(session.dateTime, event);

      const daysUntilEvent = differenceInDays(sessionDate, referenceDate);

      return daysUntilEvent >= 0 && this.hasAccessBasedOnRating(rating, daysUntilEvent);
    }).every((hasAccess) => hasAccess);
  }

  private hasAccessBasedOnRating(rating: number, daysUntilEvent: number): boolean {
    const allowedDays = CheckRatingBasedTicketAccessRule.RATING_DAYS_MAP[
      rating as keyof typeof CheckRatingBasedTicketAccessRule.RATING_DAYS_MAP
    ] || CheckRatingBasedTicketAccessRule.RATING_DAYS_MAP[CheckRatingBasedTicketAccessRule.DEFAULT_RATING];

    // User has access if days until event is LOWER than or equal to allowed days
    return daysUntilEvent <= allowedDays;
  }

}
